<!-- =========================================================================================
    File Name: StatisticsCard.vue
    Description: Statistics card component
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
      Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <vx-card class="overflow-hidden stats-card-line" :class="{'h-100': customCard}">
    <div slot="no-body" class="stats-card-line__surface" :class="`bg-${baseColor} text-${color}`">
      <div
        class="stats-card-line__body"
        :class="{
          'flex justify-between flex-row-reverse items-center': iconRight,
          'h-7rem': addHeight,
          'text-center': !iconRight && hideChart,
          'pb-0': !hideChart,
          'p-2': customCard,
          'p-6': !customCard
        }"
      >
        <feather-icon
          :icon="icon"
          class="inline-flex rounded-full stats-card-line__icon"
          :class="[`text-${color}`, {'mb-4': !iconRight}, {'p-3' : !customCard}]"
          :style="{background: `rgba(var(--vs-${color}),.15)`}"
        ></feather-icon>
        <div class="stats-card-line__text">
          <div class="stats-card-line__stat" :class="`text-${color}`">
            {{ displayStatistic }}
          </div>
          <div class="stats-card-line__title">
            {{ statisticTitle }}
          </div>
        </div>
      </div>

      <div class="line-area-chart" v-if="!hideChart">
        <vue-apex-charts
          ref="apexChart"
          :type="type"
          height="100"
          width="100%"
          :options="chartOptions"
          :series="chartData"
        />
      </div>
    </div>
  </vx-card>
</template>

<script>
import chartConfigs from "./chartConfigs.js";
import _color from "@/assets/utils/color.js";

export default {
  props: {
    icon: {
      type: String,
      required: true,
    },
    baseColor: {
      type: String,
      required: false,
    },
    statistic: {
      required: false,
      default: 0,
    },
    statisticTitle: {
      type: String,
    },
    chartData: {
      // type: Array,
      // required: true
    },
    color: {
      type: String,
      default: "primary",
    },
    colorTo: {
      type: String,
    },
    // chartType: {
    //     type: String,
    //     default: 'line-chart',
    // },
    type: {
      type: String,
      default: "line",
    },
    iconRight: {
      type: Boolean,
      default: false,
    },
    customCard: {
      type: Boolean,
      default: false,
    },
    hideChart: {
      type: Boolean,
      default: false,
    },
    addHeight: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartOptions: null,
    };
  },
  watch: {
    themePrimaryColor() {
      this.$refs.apexChart.updateOptions({
        theme: { monochrome: { color: this.getHex(this.color) } },
      });
    },
  },
  computed: {
    displayStatistic() {
      return this.statistic == null ? 0 : this.statistic;
    },
    themePrimaryColor() {
      return this.$store.state.themePrimaryColor;
    },
  },
  methods: {
    getHex(color) {
      if (_color.isColor(color)) {
        let rgb = window
          .getComputedStyle(document.documentElement)
          .getPropertyValue(`--vs-${color}`);
        rgb = rgb.split(",");
        return (
          "#" +
          (
            (1 << 24) +
            (Number(rgb[0]) << 16) +
            (Number(rgb[1]) << 8) +
            Number(rgb[2])
          )
            .toString(16)
            .slice(1)
        );
      }
      return color;
    },
    gradientToColor(color) {
      let gradientToColors = {
        primary: "#A9A2F6",
        success: "#55DD92",
        warning: "#ffc085",
        danger: "#F97794",
      };

      return gradientToColors[color]
        ? gradientToColors[color]
        : gradientToColors["primary"];
    },
  },
  created() {
    if (this.type == "area") {
      // assign chart options
      this.chartOptions = Object.assign({}, chartConfigs.areaChartOptions);

      this.chartOptions["theme"] = {
        monochrome: {
          enabled: true,
          color: this.getHex(this.color),
          shadeTo: "light",
          shadeIntensity: 0.65,
        },
      };
    } else if (this.type == "line") {
      // Assign chart options
      this.chartOptions = JSON.parse(
        JSON.stringify(chartConfigs.lineChartOptions)
      );

      this.chartOptions.fill.gradient.gradientToColors = [
        this.gradientToColor(this.colorTo || this.color),
      ];
      this.chartOptions.colors = [this.getHex(this.color)];
    }
  },
};
</script>

<style lang="scss" scoped>
.stats-card-line {
  border-radius: 14px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  will-change: transform;
}

.stats-card-line:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.12);
}

.stats-card-line__surface {
  position: relative;
}

.stats-card-line__surface::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  // subtle highlight to make colored cards feel less flat
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0) 55%
  );
}

.stats-card-line__body {
  position: relative;
  z-index: 1;
  gap: 12px;
}

.stats-card-line__icon {
  flex: 0 0 auto;
}

.stats-card-line__text {
  min-width: 0;
}

.stats-card-line__stat {
  font-weight: 700;
  letter-spacing: -0.02em;
  font-size: 1.25rem;
  line-height: 1.2;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stats-card-line__title {
  font-size: 0.8125rem;
  opacity: 0.9;
  line-height: 1.2;
  // allow long titles to show without cutting off the end
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.stats-card-line :deep(.vx-card__body) {
  padding: 0 !important;
}

.stats-card-line :deep(.apexcharts-canvas) {
  // reduce visual jitter as charts animate
  transform: translateZ(0);
}

.bg-yellow {
  background-color: #ffcd00;
}
.bg-green {
  background-color: #016929;
}
.h-100 {
  height: 100px;
}
.h-7rem {
  height: 6.7rem;
}
</style>
